import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Mail, ArrowRight, AlertCircle, UserPlus } from 'lucide-react';
import { auth } from '@/src/lib/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function AdminLogin() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        // For security in this demo, we only allow the specific user email to sign up as admin
        // In a real app, you'd have a more complex invitation system
        if (email !== "fabiyananik@agrandintl.com") {
          throw new Error("Unauthorized: Only the primary intelligence officer can initialize this system.");
        }
        await createUserWithEmailAndPassword(auth, email, password);
      }
      navigate('/admin');
    } catch (err: any) {
      setError(err.message || 'Failed to authenticate. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      <div className="absolute inset-0 cyber-grid opacity-10" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 blur-[120px] rounded-full" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <div className="glass-card rounded-[2.5rem] p-12 border-white/10 space-y-10">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mx-auto">
              {isLogin ? <Shield className="w-8 h-8" /> : <UserPlus className="w-8 h-8" />}
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight">
              Admin <span className="text-accent">{isLogin ? 'Access' : 'Initialization'}</span>
            </h1>
            <p className="text-muted-foreground text-sm font-light">
              {isLogin ? 'Secure intelligence portal. Authorized personnel only.' : 'Initialize primary admin credentials.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Intelligence ID</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input 
                    type="email"
                    required
                    placeholder="admin@agrandintl.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-14 bg-white/5 border-white/10 rounded-xl focus:border-accent/50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Access Key</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input 
                    type="password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 h-14 bg-white/5 border-white/10 rounded-xl focus:border-accent/50"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3 text-red-500 text-xs font-bold">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-14 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold shadow-[0_0_30px_rgba(0,150,255,0.3)]"
              >
                {loading ? "Authenticating..." : (isLogin ? "Establish Secure Link" : "Initialize Account")} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="w-full text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors"
              >
                {isLogin ? "Need to initialize account?" : "Back to secure access"}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
